<template>
  <VLayout>
    <VTextField
      :id="id"
      :ref="ref"
      v-model="inputValue"
      :class="['ns-v-input', 'flex-column', type, { 'with-icon': icon }]"
      :data-testid="dataTestid"
      :rules="rules.length > 0 ? rules : requiredRules"
      :type="type"
      :disabled="disabled"
      :placeholder="placeholder"
      :required="required"
      :validate-on-blur="validateOnBlur"
      :name="name"
      :aria-describedby="id"
      @blur="onBlur()"
    >
      <template slot="label">
        <span class="field-label">{{ label }}</span>
      </template>
      <template slot="prepend-inner">
        <NsIcon v-if="icon" :icon-name="icon" class="inner-icon" />
      </template>
    </VTextField>
  </VLayout>
</template>

<script>
/**
 * Original Author: Aaron Turnblom
 * Original Team: Area 51
 */
// Using `import uuid from 'uuid/v4';` will sometimes cause an error when
// @nuskin/design-components is being imported into another package.
import { v4 as uuid } from 'uuid';
import { VLayout, VTextField } from 'vuetify/lib';
import { NsIcon } from '@nuskin/ns-icon';

export default {
  components: {
    NsIcon,
    VTextField,
    VLayout,
  },
  props: {
    label: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    /** decide to validate on blur or not */
    validateOnBlur: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: () => uuid(),
    },
    dataTestid: {
      type: String,
      default: '',
    },
    ref: {
      type: String,
      default: 'input',
    },
    icon: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    requiredLabel: {
      type: String,
      default: 'required',
    },
    type: {
      type: String,
      default: 'text',
    },
    value: {
      type: String,
      default: '',
    },
    pattern: {
      type: String,
      default: '',
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      inputValue: this.value,
    };
  },
  computed: {
    requiredRules() {
      if (this.required) {
        return [(v) => !!v || this.requiredLabel];
      }
      return [];
    },
  },
  watch: {
    inputValue(val) {
      /**
       * The input event, handled for v-model support.
       *
       * @event input
       * @type {string}
       */
      this.$emit('input', val);
    },
    value(val) {
      this.inputValue = val;
    },
  },
  methods: {
    onBlur() {
      this.$emit('input', this.inputValue);
    },
  },
};
</script>

<style scoped lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/colors";

.helper-text {
  width: 100%;
  margin-top: 6px;
  color: var(--dryink);
  font-size: 0.875rem;
}
.text-field__container--disabled .helper-text {
  color: var(--eyore);
}
label {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  display: inline-block;
  margin-bottom: 6px;
  line-height: 16px !important;
}
input[type='text'] {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.text-field__container--disabled {
  label {
    color: var(--eyore);
  }
  :-ms-input-placeholder {
    color: var(--eyore);
  }
  ::-ms-input-placeholder {
    color: var(--eyore);
  }
  ::placeholder {
    color: var(--eyore);
  }
  .helper-text {
    color: var(--eyore);
  }
}
::v-deep {
  .ns-v-input {
    input[type='text']:focus {
      border: 1px solid map.get(colors.$theme, 'accent-color-b') !important;
    }
  }
}
.text-field__container-inner {
  position: relative;
  display: flex;
  align-items: center;
}
.inner-icon {
  height: 1.5rem;
  width: 1.5rem;
  position: absolute;
  left: 1rem;
  top: 2rem;
  @media screen and (max-width: 767px) {
    top: 2.5rem;
  }
  z-index: 2;
}

.clear-button {
  position: absolute;
  height: 1.5rem;
  width: 1.5rem;
  right: 1rem;
}
</style>
<style lang="scss">
.flex-column.text.with-icon input {
  padding-left: 35px !important;
}
</style>
